'use client'

import { useEffect, useLayoutEffect, useRef } from 'react'

/**
 * In-memory scroll continuity for lists. Adopted from R1's
 * `useDashboardScroll` (commits 47d61800 and 3d9ee17c), renamed, with one
 * fix: a scroll event that arrives after the list is gone is ignored.
 *
 * `key` identifies a list visit (`listScrollKey` in
 * lib/navigation/list-context.ts); pass null on details. Detail routes do not
 * overwrite list positions; returning by menu or browser history uses the
 * same key. No profile data or history state is persisted outside this tree.
 *
 * It restores `[data-scroll-container]` elements (or their Radix viewport),
 * not the window: `html` and `body` are `overflow: hidden` in this app, so
 * the frame's content panel must carry `data-scroll-container`.
 */
/** Lists whose saved position the next visit drops (`forgetListScroll`). */
const forgotten = new Set<string>()

/**
 * The next visit to this list starts at the top instead of where it was
 * left: after a task that puts a new item first (a request just sent), the
 * member should see it, not the old position.
 */
export function forgetListScroll(key: string | null) {
  if (key) forgotten.add(key)
}

/**
 * `restore: false` only records (a detail page: it opens where the browser
 * puts it); the position comes back only on a visit that passes `true`
 * (a list, or a return from a task page).
 */
export function useListScroll(key: string | null, restore = true) {
  const restoreRef = useRef(restore)
  useLayoutEffect(() => {
    restoreRef.current = restore
  }, [restore])
  const positions = useRef(new Map<string, Map<string, number>>())
  // The key on screen, updated during the commit. When a detail replaces the
  // list inside the same panel, the browser clamps the panel's scrollTop and
  // fires `scroll` before the list's passive effect is cleaned up; without
  // this check that late event saved 0 as the list's position (seen headed).
  const liveKey = useRef(key)
  useLayoutEffect(() => {
    liveKey.current = key
  }, [key])

  useEffect(() => {
    if (!key) return
    if (forgotten.delete(key)) positions.current.delete(key)
    const saved = positions.current.get(key) ?? new Map<string, number>()
    positions.current.set(key, saved)
    // Read at the visit, not on every render: the flag that says "this is a
    // return" is consumed right after, and must not cancel the restore.
    const restoring = restoreRef.current
    const pending = restoring ? new Map(saved) : new Map<string, number>()

    const scrollers = () =>
      Array.from(document.querySelectorAll<HTMLElement>('[data-scroll-container]')).flatMap(
        (root) => {
          const element =
            root.querySelector<HTMLElement>(':scope > [data-radix-scroll-area-viewport]') ?? root
          return element.getClientRects().length
            ? [{ id: root.dataset.scrollContainer!, element }]
            : []
        }
      )

    // An unseen destination starts at the top even if React reuses its scroll
    // element (for example switching Groups to Events in the same panel).
    if (restoring) {
      for (const { id } of scrollers()) {
        if (!pending.has(id)) pending.set(id, 0)
      }
    }

    const restore = () => {
      for (const { id, element } of scrollers()) {
        const top = pending.get(id)
        if (top === undefined) continue
        element.scrollTop = top
        if (Math.abs(element.scrollTop - top) < 1) pending.delete(id)
      }
      if (pending.size === 0) observer.disconnect()
    }
    const observer = new MutationObserver(restore)
    if (pending.size) observer.observe(document.body, { childList: true, subtree: true })
    const frame = requestAnimationFrame(restore)

    const record = (event: Event) => {
      if (liveKey.current !== key) return
      const target = event.target
      for (const { id, element } of scrollers()) {
        if (element === target && !pending.has(id)) saved.set(id, element.scrollTop)
      }
    }
    // A user who starts scrolling takes control, even while streamed rows
    // are still arriving. Never pull them back to an old position afterwards.
    const cancelRestore = () => {
      pending.clear()
      observer.disconnect()
    }
    document.addEventListener('scroll', record, true)
    document.addEventListener('wheel', cancelRestore, { passive: true })
    document.addEventListener('touchstart', cancelRestore, { passive: true })
    document.addEventListener('keydown', cancelRestore)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      document.removeEventListener('scroll', record, true)
      document.removeEventListener('wheel', cancelRestore)
      document.removeEventListener('touchstart', cancelRestore)
      document.removeEventListener('keydown', cancelRestore)
    }
  }, [key])
}
